import { useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { Button } from '@/shared/ui/button';
import { ErrorState } from '@/shared/feedback';
import type { PlatformAiSettings, ProviderSummary } from '@/api/schemas/ai';
import {
  DeploymentAllowancesFields,
  DeploymentDefaultsFields,
  DeploymentGenerationFields,
  DeploymentPolicyFields,
} from './deployment-settings-fields';
import {
  toDeploymentFormState,
  toDeploymentSettingsValues,
} from './deployment-settings-types';
import type {
  DeploymentFormChange,
  DeploymentFormState,
  UpdatePlatformSettingsValues,
} from './deployment-settings-types';

/**
 * The deployment-wide AI settings form.
 */
export type DeploymentSettingsFormProps = {
  settings: PlatformAiSettings;
  providers: ReadonlyArray<ProviderSummary>;
  isSaving: boolean;
  saveError: Error | null;
  onSubmit: (values: UpdatePlatformSettingsValues) => void;
};

export function DeploymentSettingsForm(props: DeploymentSettingsFormProps): ReactNode {
  const [state, setState] = useState<DeploymentFormState>(() => toDeploymentFormState(props.settings));

  const update: DeploymentFormChange = (key, value) => {
    setState((previous) => ({ ...previous, [key]: value }));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    props.onSubmit(toDeploymentSettingsValues(state));
  };

  const providerOptions = props.providers.map((provider) => ({
    value: provider.id,
    label: provider.name,
  }));

  return (
    <form className="space-y-6" onSubmit={handleSubmit} noValidate>
      {props.saveError ? (
        <ErrorState title="Could not save the deployment settings" message={props.saveError.message} />
      ) : null}

      <DeploymentDefaultsFields
        values={state}
        providerOptions={providerOptions}
        onChange={update}
      />

      <DeploymentGenerationFields values={state} onChange={update} />

      <DeploymentAllowancesFields values={state} onChange={update} />

      <DeploymentPolicyFields values={state} onChange={update} />

      <Button type="submit" loading={props.isSaving}>
        Save deployment settings
      </Button>
    </form>
  );
}
